<script lang="ts" setup>
import { goBack } from '@/composables/useNavigation';
import { SelectOptionsService, toast, toastServiceError } from '@/services';
import WorkScheduleService from '@/services/api/WorkScheduleService';
import { useUiStore } from '@/stores';
import {
    FormAction,
    Permission,
    SelectOptionsServiceGetUsersResult,
    WorkScheduleFormState,
    WorkScheduleModel,
} from '@/types';
import useVuelidate, { Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { AxiosResponse } from 'axios';
import { computed, onMounted, reactive, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const uiStore = useUiStore();
const route = useRoute();
const { t } = useI18n();

const props = defineProps({
    action: { type: String, default: FormAction.CREATE },
});

const formState = reactive<WorkScheduleFormState>({
    name: null,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    timeBegin: '08:00:00',
    timeEnd: '17:00:00',
    isActive: true,
    users: [],
});

const rules = computed<ValidationArgs<WorkScheduleFormState>>(() => ({
    name: { required, maxLength: maxLength(50) },
    monday: { required },
    tuesday: { required },
    wednesday: { required },
    thursday: { required },
    friday: { required },
    saturday: { required },
    sunday: { required },
    timeBegin: { required },
    timeEnd: { required },
    isActive: { required },
    users: {},
}));

const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const v$: Ref<Validation<ValidationArgs<WorkScheduleFormState>>> = useVuelidate(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchWorkScheduleById = async (id: number) => {
    console.log(`buscando item id ${id}`);
    try {
        setLoading(true);
        const { data } = await WorkScheduleService.get<WorkScheduleModel>(id);
        Object.assign(formState, data);
        formState.users = (data.users || []).map((user) => user.id);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                has_extension: true,
                is_active: true,
            },
        });
        usersOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const isTimeBefore = (start: string, end: string): boolean => {
    const [h1, m1] = start.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    return h1 < h2 || (h1 == h2 && m1 < m2);
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    const noDaySelected = !(
        formState.monday ||
        formState.tuesday ||
        formState.wednesday ||
        formState.thursday ||
        formState.friday ||
        formState.saturday ||
        formState.sunday
    );

    if (noDaySelected) {
        toastServiceError(t('workSchedule.error.dayNotSelected'));
        return;
    }

    if (!isTimeBefore(formState.timeBegin, formState.timeEnd)) {
        toastServiceError(t('workSchedule.error.invalidTimeRange'));
        return;
    }

    try {
        setLoading(true);
        let response: AxiosResponse;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await WorkScheduleService.update(id, formState);
            toast.success(response.data.message);
            goBack({ name: 'ViewWorkScheduleById', params: { id } });
        } else {
            response = await WorkScheduleService.create(formState);
            toast.success(response.data.message);
            goBack({ name: 'ViewWorkScheduleById', params: { id: response.data.result.id } });
        }

        v$.value.$reset();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        const workScheduleId = Number(route.params.id);
        if (!isNaN(workScheduleId)) fetchWorkScheduleById(workScheduleId);
    }
});
</script>

<template>
    <form class="space-y-4 p-4 sm:p-6 lg:p-8" autocomplete="off" @submit.prevent="onSubmit">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('workSchedule.' + props.action + '.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListWorkSchedule' }" />
                    <UiButton
                        v-if="
                            (props.action == FormAction.CREATE &&
                                $can(Permission.CREATE_WORK_SCHEDULE)) ||
                            (props.action == FormAction.EDIT && $can(Permission.EDIT_WORK_SCHEDULE))
                        "
                        type="submit"
                        variant="primary"
                        text-variant="white"
                    >
                        {{ $t('core.actions.Save') }}
                    </UiButton>
                </div>
            </template>
        </UiPageHeader>
        <UiPanel class="space-y-4">
            <div class="gap-4 md:grid md:grid-cols-8">
                <UiTextInput
                    v-model="v$.name.$model"
                    name="name"
                    type="text"
                    :label="$t('workSchedule.labels.name')"
                    class="md:col-span-6 lg:col-span-4"
                    :errors="v$.name.$errors"
                    @blur="v$.name.$touch"
                />
            </div>

            <div>
                <label for="users" class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('workSchedule.labels.users') }}
                </label>
                <UiMultiselect
                    id="users"
                    name="users"
                    label="name"
                    value-prop="id"
                    v-model="formState.users"
                    multiple
                    primitive
                    :options="usersOptions"
                />
            </div>

            <div class="grid grid-cols-2 gap-4 md:grid-cols-8">
                <UiTimePicker
                    v-model="v$.timeBegin.$model"
                    name="timeBegin"
                    :label="$t('workSchedule.labels.timeBegin')"
                    class="md:col-span-3 lg:col-span-2"
                    :errors="v$.timeBegin.$errors"
                    @blur="v$.timeBegin.$touch"
                />

                <UiTimePicker
                    v-model="v$.timeEnd.$model"
                    name="timeEnd"
                    :label="$t('workSchedule.labels.timeEnd')"
                    class="md:col-span-3 lg:col-span-2"
                    :errors="v$.timeEnd.$errors"
                    @blur="v$.timeEnd.$touch"
                />
            </div>

            <div class="space-y-2">
                <h3 class="text-sm font-medium text-gray-800">
                    {{ $t('workSchedule.labels.workDays') }}
                </h3>
                <div class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
                    <UiCheckboxInput
                        v-model="v$.monday.$model"
                        name="monday"
                        :label="$t('workSchedule.labels.monday')"
                    />

                    <UiCheckboxInput
                        v-model="v$.tuesday.$model"
                        name="tuesday"
                        :label="$t('workSchedule.labels.tuesday')"
                    />

                    <UiCheckboxInput
                        v-model="v$.wednesday.$model"
                        name="wednesday"
                        :label="$t('workSchedule.labels.wednesday')"
                    />

                    <UiCheckboxInput
                        v-model="v$.thursday.$model"
                        name="thursday"
                        :label="$t('workSchedule.labels.thursday')"
                    />

                    <UiCheckboxInput
                        v-model="v$.friday.$model"
                        name="friday"
                        :label="$t('workSchedule.labels.friday')"
                    />

                    <UiCheckboxInput
                        v-model="v$.saturday.$model"
                        name="saturday"
                        :label="$t('workSchedule.labels.saturday')"
                    />

                    <UiCheckboxInput
                        v-model="v$.sunday.$model"
                        name="sunday"
                        :label="$t('workSchedule.labels.sunday')"
                    />
                </div>
            </div>

            <UiCheckboxInput
                v-model="v$.isActive.$model"
                name="isActive"
                :label="$t('workSchedule.labels.isActive')"
            />
        </UiPanel>
        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </form>
</template>
